import { MentionComposer, MentionBody } from "./MentionComposer";
import { mentionText, mentionsIn } from "../shared/mentions";
import { type TLRichText, toRichText, type TLComment, type TLCommentThread, type TLShape } from "@tldraw/tlschema";
import { useValue } from "@tldraw/state-react";
import { useDiscussionSeen } from "./useDiscussionSeen";
import { changeSignature } from "../shared/change-facts";
import { ReviewControls, ThreadActions } from "./ReviewControls";
import { useEffect, useRef, useState } from "react";
import { Link2, MessageSquare, MoreVertical, Send, X } from "lucide-react";
import type { Point } from "../shared/schema";
import { shapePointAnchor } from "./comment-anchor";
import type { WorkspaceModel } from "./useWorkspace";
import { notify } from "./api";
import { callAgent, callsAgent, useAgentThreads } from "./agent-threads";
import {
  AgentReplyActions,
  CommentAuthor,
  CommentAuthorMark,
  ThreadAgentStatus,
} from "./AgentReply";
import { agentReplyMeta } from "../shared/agent-comments";
import {
  commentRegionPagePoints,
  commentRegionPin,
  commentRegionPoints,
  commentRegionForStroke,
  commentRegionRecord,
  commentStrokeLength,
  clampCommentPoint,
  commentStrokePagePoints,
  type CommentRegion,
} from "./comment-regions";

type CommentPlacement = {
  id: string;
  pin: Point;
  regionPoints?: Point[];
  strokePoints?: Point[];
};

// Each new comment gets a fresh composer, so the caret lands after a prefilled @Agent.
const pendingKeys = new WeakMap<object, number>();
let nextPendingKey = 0;
const pendingKey = (pending: object) => {
  if (!pendingKeys.has(pending)) pendingKeys.set(pending, nextPendingKey++);
  return pendingKeys.get(pending)!;
};

const pinColors = ["#58463f", "#91bd47", "#b91c1c", "#36a6db", "#171717"];
const colorNames = ["Brown", "Green", "Red", "Blue", "Black"];

function authorInitial(name: string) {
  return name.trim().charAt(0).toLocaleUpperCase() || "?";
}

function commentTime(createdAt: number, full = false) {
  if (full) {
    const today =
      new Date().toDateString() === new Date(createdAt).toDateString();
    return `${today ? "Today" : new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(createdAt)}, ${new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(createdAt)}`;
  }
  const minutes = Math.max(0, Math.floor((Date.now() - createdAt) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function screenRegion(
  model: Pick<WorkspaceModel, "editor">,
  target: Parameters<WorkspaceModel["editor"]["getShapePageTransform"]>[0] | undefined,
  region: CommentRegion,
  stroke?: Point[],
) {
  // Without a target the region is already in page space.
  const transform = target ? model.editor.getShapePageTransform(target) : undefined;
  if (target && !transform) return null;
  const toPage = (point: Point) => transform ? transform.applyToPoint(point) : point;
  const pagePoints = commentRegionPoints(region).map(toPage);
  const pageStroke = stroke?.map(toPage);
  const pinIndex = region.pinY ? (region.pinX ? 2 : 3) : region.pinX ? 1 : 0;
  return {
    regionPoints: pagePoints.map((point) => model.editor.pageToScreen(point)),
    strokePoints: pageStroke?.map((point) => model.editor.pageToScreen(point)),
    pin: model.editor.pageToScreen(
      pageStroke?.[pageStroke.length - 1] || pagePoints[pinIndex],
    ),
  };
}

export function CommentPins({
  model,
  allowCopyLink = true,
  clipToViewport = false,
  dockThread = false,
}: {
  model: WorkspaceModel;
  allowCopyLink?: boolean;
  clipToViewport?: boolean;
  dockThread?: boolean;
}) {
  useDiscussionSeen(model);
  const agents = useAgentThreads();
  const {
    editor,
    threads,
    comments,
    threadId,
    setThreadId,
    pending,
    setPending,
    message,
    setMessage,
    reply,
    setReply,
    canComment,
    addComment,
    addReply,
    textOf,
    user,
    commentAreaDraft,
  } = model;
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState<TLRichText>(toRichText(""));
  const [showRemoved, setShowRemoved] = useState(false);
  const [reposition, setReposition] = useState<"point" | "region" | null>(null);
  const [stroke, setStroke] = useState<Point[]>([]);
  const drawingStroke = useRef<Point[]>([]);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const placements = useValue(
    "comment placements",
    () =>
      threads
        .filter((t) => !t.resolved)
        .flatMap<CommentPlacement>((t) => {
          let p: Point;
          if (t.anchor.type === "shape") {
            const s = editor.getShape(t.anchor.shapeId);
            if (!s) return [];
            const b = editor.getShapeGeometry(s).bounds;
            p = editor
              .getShapePageTransform(s)!
              .applyToPoint({ x: t.anchor.x * b.w, y: t.anchor.y * b.h });
          } else if (t.anchor.type === "point") p = t.anchor;
          else if (t.anchor.type === "region") {
            const pagePoints = commentRegionPagePoints(editor, t);
            if (!pagePoints) return [];
            const strokePoints = commentStrokePagePoints(editor, t);
            return [
              {
                id: t.id,
                pin: editor.pageToScreen(
                  strokePoints?.[strokePoints.length - 1] ||
                    commentRegionPin(t, pagePoints),
                ),
                regionPoints: pagePoints.map((point) =>
                  editor.pageToScreen(point),
                ),
                strokePoints: strokePoints?.map((point) =>
                  editor.pageToScreen(point),
                ),
              },
            ];
          } else return [];
          return [{ id: t.id, pin: editor.pageToScreen(p) }];
        }),
    [editor, threads],
  );
  const pendingPlacement = useValue<{
    pin: Point;
    regionPoints?: Point[];
    strokePoints?: Point[];
  } | null>(
    "new comment placement",
    () => {
      if (!pending) return null;
      if (pending.kind === "region")
        return screenRegion(
          model,
          pending.target?.id,
          pending.region,
          pending.stroke,
        );
      if (!pending.shape) return { pin: editor.pageToScreen(pending.p) };
      if (!editor.getShape(pending.shape.id)) return null;
      return {
        pin: editor.pageToScreen(
          editor
            .getShapePageTransform(pending.shape.id)!
            .applyToPoint(pending.p),
        ),
      };
    },
    [editor, pending],
  );
  const draftPlacement = useValue(
    "comment area draft",
    () =>
      commentAreaDraft
        ? screenRegion(
            model,
            commentAreaDraft.target.id,
            commentAreaDraft.region,
            commentAreaDraft.stroke,
          )
        : null,
    [editor, commentAreaDraft],
  );
  const viewport = useValue(
    "comment viewport",
    () => {
      const bounds = editor.getViewportScreenBounds();
      if (!clipToViewport) return bounds;
      return {
        x: bounds.x,
        y: Math.max(0, bounds.y),
        w: bounds.w,
        maxX: bounds.maxX,
        maxY: Math.min(window.innerHeight, bounds.maxY),
      };
    },
    [editor, clipToViewport],
  );
  const viewportClip = clipToViewport
    ? {
        clipPath: `inset(${Math.max(0, viewport.y)}px ${Math.max(0, window.innerWidth - viewport.maxX)}px ${Math.max(0, window.innerHeight - viewport.maxY)}px ${Math.max(0, viewport.x)}px)`,
      }
    : undefined;
  const visibleId = model.panel === "comments" ? null : threadId || hoverId;
  const placement = placements.find((p) => p.id === visibleId);
  const thread = threads.find((t) => t.id === visibleId && !t.resolved);
  const position = pendingPlacement?.pin || placement?.pin;
  const close = () => {
    setThreadId(null);
    setHoverId(null);
    setPending(null);
  };
  useEffect(() => {
    const outside = (e: PointerEvent) => {
      if (dockThread) return;
      if (
        !(e.target as Element).closest?.(
          ".comment-popover, .comment-pin, .comment-hover-card, .comment-region, .comment-area-mark, .comments-list, .comment-reposition",
        )
      ) {
        setThreadId(null);
        setHoverId(null);
      }
    };
    const escape = (e: KeyboardEvent) => {
      if (dockThread && !(e.target as Element).closest?.(".comment-popover")) return;
      if (e.key === "Escape") {
        setThreadId(null);
        setHoverId(null);
        setPending(null);
        setReposition(null);
      }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [setThreadId, setPending, dockThread]);
  useEffect(() => () => clearTimeout(leaveTimer.current), []);
  const enter = (id: string) => {
    clearTimeout(leaveTimer.current);
    setHoverId(id);
  };
  const leave = () => {
    leaveTimer.current = setTimeout(() => setHoverId(null), 160);
  };
  const open = (id: string) => {
    setThreadId(id);
    setReply(toRichText(""));
    model.setPanel(null);
    setPending(null);
  };
  const isOpen = !!pending || (!!threadId && !!thread);
  const width = Math.min(isOpen ? 340 : 286, viewport.w - 24);
  const left = position
    ? Math.max(
        viewport.x + 12,
        Math.min(position.x + (isOpen ? 60 : 24), viewport.maxX - width - 12),
      )
    : 0;
  const top = position
    ? Math.max(
        viewport.y + 85,
        Math.min(
          position.y - (isOpen ? 90 : 22),
          viewport.maxY - (isOpen ? 230 : 78),
        ),
      )
    : 0;
  // A small embedded canvas can clamp the hover card over its clickable pin.
  const hoverTop =
    clipToViewport && position && left < position.x + 22 && left + width > position.x - 22
      ? position.y + 104 < viewport.maxY
        ? position.y + 26
        : position.y - 104
      : top;
  const messages = comments
    .filter((c) => c.threadId === visibleId)
    .sort((a, b) => a.createdAt - b.createdAt);
  const removedMessages = useValue("removed comments", () => editor.store.allRecords()
    .filter((record): record is TLComment => record.typeName === "comment" && record.threadId === visibleId && record.isDeleted && record.authorId === user.id)
    .sort((a, b) => a.createdAt - b.createdAt), [editor, visibleId, user.id]);
  const previewMessage = messages[messages.length - 1];
  const badgeColor = pinColors.includes(String(thread?.meta.badgeColor))
    ? String(thread?.meta.badgeColor)
    : pinColors[0];
  const changeColor = (color: string) => {
    if (!thread || !canComment || thread.createdBy !== user.id) return;
    editor.markHistoryStoppingPoint("Change comment colour");
    editor.store.put([
      { ...thread, meta: { ...thread.meta, badgeColor: color } },
    ]);
  };
  const copyLink = async () => {
    if (!thread) return;
    const url = new URL(location.href);
    url.searchParams.set("thread", thread.id);
    try {
      await navigator.clipboard.writeText(url.toString());
      notify("Comment link copied.");
    } catch {
      notify("Couldn't copy the link. Please try again.");
    }
  };
  const pendingIsRegion = pending?.kind === "region";
  const repositionThread = threadId ? threads.find((item) => item.id === threadId) : undefined;
  const canMoveThread = !!(canComment && repositionThread?.createdBy === user.id);
  const saveAnchor = (point: Point) => {
    if (!repositionThread || !canMoveThread) return;
    const shape = editor.getShapeAtPoint(point, { hitInside: true });
    const local = shape ? editor.getPointInShapeSpace(shape, point) : point;
    const bounds = shape ? editor.getShapeGeometry(shape).bounds : null;
    const anchor = shape && bounds ? {
      type: "shape" as const,
      shapeId: shape.id,
      isPrecise: true,
      ...shapePointAnchor(local, bounds),
    } : { type: "point" as const, x: point.x, y: point.y };
    editor.markHistoryStoppingPoint("Move comment anchor");
    editor.store.put([{ ...repositionThread, anchor }]);
    setReposition(null);
    notify(shape ? "Discussion attached to this object." : "Discussion moved on the canvas.");
  };
  const regionTarget = (): TLShape | null => {
    const stored = repositionThread?.meta.reviewRegion;
    if (!stored || typeof stored !== "object" || Array.isArray(stored)) return null;
    const id = (stored as { shapeId?: string }).shapeId;
    return id ? editor.getShape(id as TLShape["id"]) ?? null : null;
  };
  const commitRegion = () => {
    const target = regionTarget();
    if (!target || !repositionThread || !canMoveThread) {
      notify("The attached drawing is unavailable.");
      setReposition(null); return;
    }
    const points = drawingStroke.current;
    if (commentStrokeLength(points) < 8 / editor.getZoomLevel()) {
      notify("Drag across an area to redraw the review mark.");
      return;
    }
    const region = commentRegionForStroke(points, 12 / editor.getZoomLevel(), editor.getShapeGeometry(target).bounds);
    const record = commentRegionRecord(editor, target, region, points);
    editor.markHistoryStoppingPoint("Redraw review area");
    editor.store.put([{ ...repositionThread, anchor: record.anchor, meta: { ...repositionThread.meta, ...record.meta } }]);
    drawingStroke.current = []; setStroke([]); setReposition(null);
    notify("Review area updated; discussion preserved.");
  };
  return (
    <>
      {reposition && <div className="comment-reposition" role="application" aria-label={reposition === "point" ? "Move comment anchor" : "Redraw review area"}
        onPointerDown={(event) => {
          if (event.button !== 0 || (event.target as Element).closest("button")) return;
          event.preventDefault(); event.stopPropagation();
          const page = editor.screenToPage({ x: event.clientX, y: event.clientY });
          if (reposition === "point") saveAnchor(page);
          else {
            const target = regionTarget();
            if (!target) { notify("The attached drawing is unavailable."); setReposition(null); return; }
            const first = clampCommentPoint(editor.getPointInShapeSpace(target, page), editor.getShapeGeometry(target).bounds);
            drawingStroke.current = [first]; setStroke([first]);
            event.currentTarget.setPointerCapture(event.pointerId);
          }
        }}
        onPointerMove={(event) => {
          if (reposition !== "region" || !drawingStroke.current.length) return;
          const target = regionTarget(); if (!target) return;
          const page = editor.screenToPage({ x: event.clientX, y: event.clientY });
          const next = clampCommentPoint(editor.getPointInShapeSpace(target, page), editor.getShapeGeometry(target).bounds);
          const last = drawingStroke.current.at(-1)!;
          if (Math.hypot(next.x - last.x, next.y - last.y) > 3 / editor.getZoomLevel()) {
            drawingStroke.current = [...drawingStroke.current.slice(-239), next]; setStroke(drawingStroke.current);
          }
        }}
        onPointerUp={(event) => {
          if (reposition === "region" && drawingStroke.current.length) commitRegion();
          if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        }}
        onPointerCancel={() => { drawingStroke.current = []; setStroke([]); }}>
        <div className="comment-reposition-instruction" onPointerDown={(event) => event.stopPropagation()}>
          <span>{reposition === "point" ? "Click an object or the canvas to place this discussion." : "Drag over the attached drawing to redraw this area."}</span>
          <button onClick={() => { setReposition(null); drawingStroke.current = []; setStroke([]); }}>Cancel</button>
        </div>
        {stroke.length > 1 && regionTarget() && <svg aria-hidden="true"><polyline points={stroke.map((point) => {
          const page = editor.getShapePageTransform(regionTarget()!).applyToPoint(point);
          const screen = editor.pageToScreen(page); return `${screen.x},${screen.y}`;
        }).join(" ")} /></svg>}
      </div>}
      <svg className="comment-regions" aria-hidden="true" style={viewportClip}>
        {!dockThread && position &&
          isOpen &&
          (position.x < left || position.x > left + width) && (
            <line
              className="comment-leader"
              x1={position.x}
              y1={position.y}
              x2={position.x < left ? left : left + width}
              y2={Math.max(top + 50, Math.min(position.y, top + 150))}
            />
          )}
        {draftPlacement?.strokePoints && (
          <polyline
            className="comment-area-mark draft"
            points={draftPlacement.strokePoints
              .map((point) => `${point.x},${point.y}`)
              .join(" ")}
          />
        )}
        {placements.map((item) =>
          item.strokePoints ? (
            <polyline
              className={`comment-area-mark ${visibleId === item.id ? "active" : ""} ${model.tool === "select" ? "clickable" : ""}`}
              key={item.id}
              points={item.strokePoints
                .map((point) => `${point.x},${point.y}`)
                .join(" ")}
              onPointerEnter={() => enter(item.id)}
              onPointerLeave={leave}
              onClick={(event) => {
                event.stopPropagation();
                open(item.id);
              }}
            />
          ) : item.regionPoints ? (
            <polygon
              className={`comment-region ${visibleId === item.id ? "active" : ""} ${model.tool === "select" ? "clickable" : ""}`}
              key={item.id}
              points={item.regionPoints
                .map((point) => `${point.x},${point.y}`)
                .join(" ")}
              onPointerEnter={() => enter(item.id)}
              onPointerLeave={leave}
              onClick={(event) => {
                event.stopPropagation();
                open(item.id);
              }}
            />
          ) : null,
        )}
        {pendingPlacement?.strokePoints?.length ? (
          <polyline
            className="comment-area-mark pending active"
            points={pendingPlacement.strokePoints
              .map((point) => `${point.x},${point.y}`)
              .join(" ")}
          />
        ) : pendingPlacement?.regionPoints && (
          <polygon
            className="comment-region active"
            points={pendingPlacement.regionPoints
              .map((point) => `${point.x},${point.y}`)
              .join(" ")}
          />
        )}
      </svg>
      <div className="comment-pins" style={viewportClip}>
        {placements.map((placement) => {
          const pinnedThread = threads.find((t) => t.id === placement.id);
          const first = comments
            .filter((c) => c.threadId === placement.id)
            .sort((a, b) => a.createdAt - b.createdAt)[0];
          const color = pinColors.includes(
            String(pinnedThread?.meta.badgeColor),
          )
            ? String(pinnedThread?.meta.badgeColor)
            : pinColors[0];
          return (
            <button
              className={`comment-pin ${threadId === placement.id ? "open" : ""}`}
              key={placement.id}
              aria-label={`Open comment ${threads.findIndex((t) => t.id === placement.id) + 1}`}
              aria-expanded={visibleId === placement.id}
              style={{
                left: placement.pin.x,
                top: placement.pin.y,
                backgroundColor: color,
              }}
              onMouseEnter={() => enter(placement.id)}
              onMouseLeave={leave}
              onFocus={() => enter(placement.id)}
              onBlur={leave}
              onClick={() =>
                threadId === placement.id ? close() : open(placement.id)
              }
            >
              {threadId === placement.id && !dockThread
                ? null
                : authorInitial(String(first?.meta.author || "Collaborator"))}
            </button>
          );
        })}
        {pendingPlacement && (
          <span
            className="comment-draft-pin"
            style={{
              left: pendingPlacement.pin.x,
              top: pendingPlacement.pin.y,
            }}
          >
            {authorInitial(user.name)}
          </span>
        )}
      </div>
      {position && thread && !isOpen && previewMessage && (
        <button
          className={`comment-hover-card${clipToViewport ? " landing-demo-comment" : ""}`}
          style={{ left, top: hoverTop, width }}
          onMouseEnter={() => clearTimeout(leaveTimer.current)}
          onMouseLeave={leave}
          onFocus={() => clearTimeout(leaveTimer.current)}
          onBlur={leave}
          onClick={() => open(thread.id)}
          aria-label={`Open discussion by ${String(previewMessage.meta.author || "Collaborator")}`}
        >
          <span
            className="comment-avatar"
            style={{ backgroundColor: badgeColor }}
          >
            {authorInitial(
              String(previewMessage.meta.author || "Collaborator"),
            )}
          </span>
          <span className="comment-hover-copy">
            <span className="comment-hover-meta">
              <strong>
                {String(previewMessage.meta.author || "Collaborator")}
              </strong>
              <time dateTime={new Date(previewMessage.createdAt).toISOString()}>
                {commentTime(previewMessage.createdAt)}
              </time>
            </span>
            <span className="comment-hover-text">
              {textOf(previewMessage.body)}
            </span>
          </span>
        </button>
      )}
      {position && (pending || (threadId && thread)) && (
        <section
          data-review-thread={!pending ? threadId ?? undefined : undefined}
          className={`comment-popover floating-surface ${pending ? "comment-new" : "comment-open"}${clipToViewport ? " landing-demo-comment" : ""}${dockThread ? " is-docked" : ""}`}
          style={dockThread ? undefined : {
            left,
            top,
            width,
            maxHeight: Math.max(160, viewport.maxY - top - 20),
            overflowY: "auto",
          }}
          role={dockThread ? "region" : "dialog"}
          aria-label={
            pendingIsRegion
              ? "New area comment"
              : pending
                ? "New point comment"
                : "Comment thread"
          }
          onMouseEnter={() => clearTimeout(leaveTimer.current)}
          onMouseLeave={leave}
        >
          {pending ? (
            <div className="workspace-panel-heading">
              <strong>
                <MessageSquare size={15} />
                {pendingIsRegion ? "Comment on area" : "Point comment"}
              </strong>
              <button aria-label="Close comment" onClick={close}>
                <X size={17} />
              </button>
            </div>
          ) : (
            <div className="comment-thread-toolbar">
              {canComment && thread && (
                <button
                  className="comment-resolve"
                  onClick={() => {
                    editor.markHistoryStoppingPoint("Resolve discussion");
                    editor.store.put([
                      { ...thread, resolved: { at: Date.now(), by: user.id } },
                    ]);
                    close();
                  }}
                >
                  <span className="comment-resolve-switch" aria-hidden="true" />
                  Resolve
                </button>
              )}
              <details className="comment-more">
                <summary aria-label="More comment options">
                  <MoreVertical size={21} />
                </summary>
                <div>
                  {allowCopyLink && <button onClick={() => void copyLink()}>
                    <Link2 size={14} /> Copy link
                  </button>}
                  {canMoveThread && repositionThread?.anchor.type !== "region" &&
                    <button onClick={() => { setReposition("point"); setHoverId(null); }}>
                      Move anchor
                    </button>}
                  {canMoveThread && repositionThread?.anchor.type === "region" &&
                    <button onClick={() => { setReposition("region"); setHoverId(null); }}>
                      Redraw area
                    </button>}
                  {canMoveThread && <div className="comment-more-colors" aria-label="Badge color">
                    <small>Badge colour</small>
                    <div className="comment-colors">
                      {pinColors.map((color, index) => (
                        <button key={color} className={color === badgeColor ? "selected" : ""}
                          style={{ backgroundColor: color }} onClick={() => changeColor(color)}
                          aria-label={`${colorNames[index]} badge`} aria-pressed={color === badgeColor} />
                      ))}
                    </div>
                  </div>}
                  <button onClick={close}>
                    <X size={14} /> Close
                  </button>
                </div>
              </details>
            </div>
          )}
          {pending ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                addComment();
              }}
            >
              <small className="comment-anchor-note">
                {pendingIsRegion
                  ? "Review mark · hidden from normal exports"
                  : pending.shape
                    ? "Attached to this object"
                    : "Placed on the canvas"}
              </small>
              <MentionComposer key={pendingKey(pending)} autoFocus label="Comment" placeholder="Share an idea or type @ to mention…" value={message} onChange={setMessage} people={model.people} error={model.peopleError} onRetry={model.retryPeople} enabled={model.room.platform === "cloudflare" || agents.enabled} agent={agents.enabled}/>

              {model.room.platform === "cloudflare" && (
                <ReviewControls
                  people={model.people}
                  value={model.review}
                  onChange={model.setReview}
                />
              )}
              <div className="comment-popover-actions">
                <button type="button" onClick={close}>
                  Cancel
                </button>
                <button className="primary" disabled={!mentionText(message).trim() || mentionText(message).length > 3000}>
                  Post comment
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="pin-messages">
                {messages.map((c) => (
                  <div className={`comment-message${agentReplyMeta(c) ? " agent-reply" : ""}`} key={c.id} data-review-comment={c.isDeleted || editingId === c.id ? undefined : c.id} data-review-signature={changeSignature(c)} data-review-occurrences={JSON.stringify(mentionsIn(c.body).map(m=>m.occurrenceId))}>
                    {agentReplyMeta(c) ? (
                      <span className="comment-avatar small agent">
                        <CommentAuthorMark comment={c} userId={user.id} size={16} />
                      </span>
                    ) : (
                      <span
                        className="comment-avatar small"
                        style={{ backgroundColor: badgeColor }}
                      >
                        {authorInitial(String(c.meta.author || "Collaborator"))}
                      </span>
                    )}
                    <div className="comment-message-copy">
                      <div className="comment-message-meta">
                        <strong>
                          <CommentAuthor comment={c} userId={user.id} />
                        </strong>
                        <time dateTime={new Date(c.createdAt).toISOString()}>
                          {commentTime(c.createdAt, true)}
                        </time>
                        {c.editedAt && <small>Edited</small>}
                      </div>
                      {editingId === c.id ? (
                        <form className="comment-edit-form" onSubmit={(event) => {
                          event.preventDefault();
                          if (!mentionText(editText).trim() || mentionText(editText).length > 3000) return;
                          editor.markHistoryStoppingPoint("Edit comment");
                          editor.store.put([{ ...c, body: editText, meta:!mentionsIn(c.body).length&&!mentionsIn(editText).length?c.meta:{...c.meta, review:{...((c.meta.review ?? {}) as object), mentions:[...new Set(mentionsIn(editText).map(m=>m.userId))]}}, editedAt: Date.now() }]);
                          // Only an @Agent added by this edit calls the agent again.
                          if (callsAgent(editText, c.threadId, c.body)) void callAgent(c.threadId, c.id);
                          setEditingId(null);
                        }}>
                          <MentionComposer label="Edit comment" placeholder="Type @ to mention…" value={editText} onChange={setEditText} people={model.people} error={model.peopleError} onRetry={model.retryPeople} enabled={model.room.platform === "cloudflare" || agents.enabled} agent={agents.enabled}/>
                          <div><button type="button" onClick={() => setEditingId(null)}>Cancel</button><button type="submit" disabled={!mentionText(editText).trim()}>Save</button></div>
                        </form>
                       ) : <div className="comment-mention-body"><MentionBody body={c.body}/>{!mentionsIn(c.body).length && Array.isArray((c.meta.review as any)?.mentions) && (c.meta.review as any).mentions.length > 0 && <small>Mentioned: {(c.meta.review as any).mentions.map((id:string)=>model.people.find(p=>p.id===id)?.name??"Former member").join(", ")}</small>}</div>}
                      <AgentReplyActions comment={c} editor={editor} canEdit={model.canEdit} />
                      {canComment && c.authorId === user.id && editingId !== c.id && (
                        <div className="comment-message-actions">
                          <button onClick={() => { setEditingId(c.id); setEditText(c.body); }}>Edit</button>
                          <button onClick={() => {
                            editor.markHistoryStoppingPoint("Remove comment");
                            editor.store.put([{ ...c, isDeleted: true }]);
                            notify("Comment removed. Open Removed comments to restore it.");
                          }}>Remove</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {threadId && <ThreadAgentStatus threadId={threadId} userId={user.id} />}
                {removedMessages.length > 0 && <details className="removed-comments" open={showRemoved} onToggle={(event) => setShowRemoved(event.currentTarget.open)}>
                  <summary>Removed comments ({removedMessages.length})</summary>
                  {removedMessages.map((comment) => <div key={comment.id}>
                    <p>{textOf(comment.body)}</p>
                    <button onClick={() => {
                      editor.markHistoryStoppingPoint("Restore comment");
                      editor.store.put([{ ...comment, isDeleted: false }]);
                    }}>Restore my comment</button>
                  </div>)}
                </details>}
              </div>
              {threadId && model.room.platform === "cloudflare" && (
                <ThreadActions
                  board={model.room.id}
                  thread={threadId}
                  canChange={canComment}
                  people={model.people}
                />
              )}
              {threadId && canComment && thread ? (
                <form
                  className="comment-reply-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    addReply(thread);
                  }}
                >
                  <div className="comment-reply-row">
                    <MentionComposer label="Reply to comment" placeholder="Reply or type @ to mention…" value={reply} onChange={setReply} people={model.people} error={model.peopleError} onRetry={model.retryPeople} enabled={model.room.platform === "cloudflare" || agents.enabled} agent={agents.enabled}/>

                    <button aria-label="Send reply" disabled={!mentionText(reply).trim() || mentionText(reply).length > 3000}>
                      <Send size={18} />
                    </button>
                  </div>
                  {model.room.platform === "cloudflare" && (
                    <ReviewControls
                      people={model.people}
                      value={model.review}
                      onChange={model.setReview}
                    />
                  )}
                </form>
              ) : null}
            </>
          )}
        </section>
      )}
    </>
  );
}
